import { IonInfiniteScroll } from '@ionic/react'
import moment from 'moment-timezone'
import { useMemo } from 'react'

import { useGetUnitControlProgramHistory } from '@apps-common/apis/portal/control'
import { InfiniteLoader } from '@apps-common/components/common/InfiniteLoader'
import { PullRefresh } from '@apps-common/components/common/PullRefresh'
import { commonIcons } from '@apps-common/config/icons'
import { IUnitControlQueueBasicModel } from '@apps-common/types/portal/unit-control'

import { HistorySkeletonCard } from './HistorySkeletonCard'
import { HistoryResultMessageDisplay } from './components/HistoryResultMessageDisplay'

const TAKE = 15

interface IProps {
  unitId?: string
}

export const ProgramHistoryView: React.FC<IProps> = ({ unitId }) => {
  const {
    isLoading: isLoadingByUnit,
    isFetching: isFetchingByUnit,
    isRefetching: isRefetchingByUnit,
    isError: isErrorByUnit,
    data: dataByUnit,
    refetch: refetchByUnit,
    fetchNextPage: fetchNextPageByUnit,
    hasNextPage: hasNextPageByUnit
  } = useGetUnitControlProgramHistory({ unitId: unitId || '', take: TAKE, enabled: unitId === undefined })

  const data = useMemo(
    () =>
      dataByUnit?.pages.reduce((acc: IUnitControlQueueBasicModel[], curr: IUnitControlQueueBasicModel[]) => [...acc, ...(curr || [])], []),
    [unitId, dataByUnit]
  )

  const loading = isLoadingByUnit || isErrorByUnit

  return (
    <>
      <PullRefresh loading={isRefetchingByUnit} callback={refetchByUnit} />
      <div className='flex flex-col gap-[10px]'>
        {(loading || !data) && Array.from(Array(TAKE).keys()).map((i) => <HistorySkeletonCard key={i.toString()} hideStatus />)}
        {!loading &&
          data &&
          data.map((d, i) => (
            <div
              key={`trigger-history-card-${d.id}-${i}`}
              className='relative overflow-hidden w-full bg-surface text-text border border-border rounded-2xl px-[15px] py-[10px] flex items-end justify-between'
            >
              <div className='w-full flex flex-col gap-[10px] items-start justify-between'>
                <div className='w-full flex items-center justify-between gap-[10px]'>
                  <div className='flex flex-col md:flex-row md:items-center gap-[5px]'>
                    <div className='text-[0.8em] leading-[1em] text-text-muted/70 pl-1'>
                      {moment(d.dateAdded).format('Do MMM YY, hh:mm a')}
                    </div>
                  </div>
                </div>
                <div className='text-[0.85em] flex items-start justify-start gap-[8px]'>
                  {d.result && (
                    <div className='bg-success text-on-success p-[5px] rounded-[12px] mt-[5px]'>
                      <commonIcons.checkCircle className='text-[1.2em]' />
                    </div>
                  )}
                  {!d.result && (
                    <div className='bg-warning text-on-warning p-[5px] rounded-[12px] mt-[5px]'>
                      <commonIcons.toastWarning className='text-[1.2em]' />
                    </div>
                  )}
                  <div className='flex flex-col gap-[2px]'>
                    <div className='text-[0.9em] px-[2px]'>{d.userName}</div>
                    <div className='text-[0.9em]'>{d.msg}</div>
                    <div className='text-[0.85em] flex flex-wrap items-center justify-start gap-[5px]'>
                      {d?.commands?.map((c) => (
                        <div
                          key={`program-history-${c.id}`}
                          className='text-[0.9em] leading-[1em] text text-muted bg-surface-2 border border-border px-[5px] py-[3px] rounded-[8px]'
                        >
                          {c.command}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        <HistoryResultMessageDisplay
          hasNoResults={!loading && !isFetchingByUnit && !hasNextPageByUnit && data && data.length === 0}
          isEndOfResults={!loading && !isFetchingByUnit && !hasNextPageByUnit && data && data.length > 0}
        />
      </div>
      <IonInfiniteScroll
        onIonInfinite={async (ev) => {
          if (!hasNextPageByUnit) {
            ev.target.complete()

            return
          }

          await fetchNextPageByUnit()

          ev.target.complete()
        }}
      >
        <InfiniteLoader loading={!loading && isFetchingByUnit} />
      </IonInfiniteScroll>
    </>
  )
}
